import { inject, Injectable } from '@angular/core';
import { Bundle, ChargeItem, Invoice, Medication, MedicationDispense, MedicationRequest, Money, PaymentReconciliation, Reference } from 'fhir/r4';
import { map, switchMap, of, catchError, Observable, forkJoin, throwError, finalize } from 'rxjs';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { ErrorService } from '../shared/error.service';
import { HttpClient, HttpContext } from '@angular/common/http';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { StateService } from '../shared/state.service';
import { AuthService } from '../shared/auth/auth.service';
import { DisableCache } from '../loading.interceptor';
import { UtilityService } from '../shared/utility.service';
import { LoaderService } from '../loader/loader.service';

@Injectable({
  providedIn: 'root'
})
export class MedicationDispenseUtilityService {
  errorService = inject(ErrorService);
  http = inject(HttpClient);
  backendEndPoint = inject(backendEndPointToken);
  terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
  snackBar = inject(MatSnackBar);
  fhirResourceService = inject(FhirResourceService);
  authService = inject(AuthService);
  dialog = inject(MatDialog);
  utilityService = inject(UtilityService);
  constructor() { }

  loaderService = inject(LoaderService);

  submitDispenseCancellationTransactionByDispenseId(dispenseId: string) {
    if (!dispenseId) {
      this.errorService.openandCloseError('dispenseId is required.');
      return;
    }

    this.loaderService.openLoader('Cancelling Dispense...');

    this.prepareDispenseCancellationTransactionByDispenseId(dispenseId)
      .pipe(
        finalize(() => this.loaderService.closeLoader()),
        switchMap((bundle) => {
          if (!bundle?.entry?.length) {
            this.errorService.openandCloseError('No cancellation operations were generated.');
            return of(null);
          }
          return this.fhirResourceService.postBundle(bundle as any);
        }),
        catchError((err) => {
          console.error('[meddispense] cancellation transaction failed', err);
          this.errorService.openandCloseError(err?.message || 'Failed to cancel dispense.');
          return of(null);
        })
      )
      .subscribe((resp) => {
        if (!resp) return;
        this.stateService.processOrgWideBundleTransaction(resp.bundle);
        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: 'Dispense cancelled successfully.' },
          duration: 3000
        });
        this.dialog.closeAll();
      });
  }

  submitDispenseCompletionTransactionByDispenseId(dispenseId: string) {
    if (!dispenseId) {
      this.errorService.openandCloseError('dispenseId is required.');
      return;
    }

    this.loaderService.openLoader('Completing Dispense...');

    this.prepareDispenseCompletionPayloadByDispenseId(dispenseId)
      .pipe(
        finalize(() => this.loaderService.closeLoader()),
        map((rows) => (rows ?? []).flat().filter(Boolean)),
        map((ops) => {
          const bundleEntries = ops
            .map((op) => {
              const method = op?.operationType;
              const resourceType = op?.resourceType;
              const obj = op?.objectToUse;

              if (!method || !resourceType || !obj) return null;

              if (method === 'PUT') {
                // obj is the full resource (must have id)
                const id = (obj as any)?.id;
                if (!id) {
                  // skip rather than failing entire transaction
                  return null;
                }
                return {
                  resource: { ...(obj as any), resourceType },
                  request: { method: 'PUT', url: `${resourceType}/${id}` }
                };
              }

              return null;
            })
            .filter(Boolean);

          return {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: bundleEntries
          } as any;
        }),
        switchMap((bundle) => {
          if (!bundle?.entry?.length) {
            this.errorService.openandCloseError('No completion operations were generated.');
            return of(null);
          }
          return this.fhirResourceService.postBundle(bundle);
        }),
        catchError((err) => {
          console.error('[meddispense] completion transaction failed', err);
          this.errorService.openandCloseError(err?.message || 'Failed to complete dispense.');
          return of(null);
        })
      )
      .subscribe((resp) => {
        if (!resp) return;
        this.stateService.processOrgWideBundleTransaction(resp.bundle);
        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: 'Dispense completed successfully.' },
          duration: 3000
        });
        this.dialog.closeAll();
      });
  } stateService = inject(StateService);

  private prepareDispenseCancellationTransactionByDispenseId(dispenseId: string): Observable<Bundle> {
    return this.http
      .get<MedicationDispense>(`${this.backendEndPoint}/MedicationDispense/${dispenseId}`, {
        //disableCache
        context: new HttpContext().set(DisableCache, true)

      })
      .pipe(
        switchMap((dispense) => {
          const dispenseStatus = (dispense?.status ?? '').toString().toLowerCase();
          if (['cancelled', 'entered-in-error'].includes(dispenseStatus)) {
            return throwError(() => new Error('Dispense is already cancelled.'));
          }

          const subjectRef = (dispense as any)?.subject?.reference as string | undefined;
          const dispenseRef = `MedicationDispense/${dispenseId}`;
          const medRefStr = this.coerceReference((dispense as any)?.medicationReference)?.reference || '';
          const medId = this.extractResourceId(medRefStr);

          const chargeItems$ = this.findChargeItemsForDispense(dispenseRef, subjectRef);
          const invoices$ = this.findInvoicesForSubject(subjectRef);
          const medication$ = medId
            ? this.http.get<Medication>(`${this.backendEndPoint}/Medication/${medId}`, {
              //disableCache
              context: new HttpContext().set(DisableCache, true)
            })
            : of(null);

          return forkJoin({ dispense: of(dispense), chargeItems: chargeItems$, invoices: invoices$, medication: medication$, medId: of(medId) });
        }),

        map(({ dispense, chargeItems, invoices, medication, medId }) => {
          const dispenseRef = `MedicationDispense/${dispenseId}`;
          // alert(JSON.stringify(dispense.quantity));
          // debugger;
          const quantity = this.parseQuantity((dispense as any)?.quantity?.value ?? (dispense as any)?.quantity);
          console.log('[tracing med inventory deduction]', 'dispense quantity:', quantity, );

          // Only increment inventory on cancellation if the dispense was already completed.
          // If the dispense is still 'in-progress' the inventory was not decremented yet
          // (this app defers inventory decrement to completion), so skip the medication PUT.
          const dispenseStatus = ((dispense as any)?.status ?? '').toString().toLowerCase();
          // alert(`dispenseStatus: ${dispenseStatus}, quantity: ${quantity}, medId: ${medId}, medication: ${!!medication}`);
          const medicationUpdate =
            medication && medId && quantity > 0 && dispenseStatus === 'completed'
              ? (() => {
                // alert(`medicationUpdate: medId=${medId}, quantity=${quantity}, adjustment=increment`);
                // debugger;
                const updated = this.buildMedicationUpdatePayload(medication, quantity, { adjustment: 'increment' });
                return updated ? { ...(updated as any), id: (updated as any)?.id ?? medId } as Medication : undefined;
              })()
              : undefined;

          const candidateChargeItems = (chargeItems ?? []).filter(ci => {
            const status = (ci?.status ?? '').toString().toLowerCase();
            if (!['billable', 'billed'].includes(status)) return false;
            const service = (ci as any)?.service;
            const refs: string[] = Array.isArray(service)
              ? service.map((s: any) => (s?.reference ?? '').toString()).filter(Boolean)
              : [];
            return refs.some(r => r.includes(dispenseRef));
          });

          const chargeItem = candidateChargeItems[0] ?? null;
          const invoice = chargeItem
            ? (invoices ?? []).find(inv => {
              const lines = (inv as any)?.lineItem ?? [];
              return Array.isArray(lines) && lines.some((l: any) => (l?.chargeItemReference?.reference ?? '') === `ChargeItem/${(chargeItem as any)?.id}`);
            }) ?? null
            : null;

          const isPaid = (invoice?.status ?? '').toString().toLowerCase() === 'balanced';

          // Always cancel the dispense
          const dispenseUpdate: MedicationDispense = {
            ...(dispense as any),
            status: 'cancelled'
          } as any;

          const entry: any[] = [];

          // PUT dispense
          entry.push({
            resource: { ...(dispenseUpdate as any), resourceType: 'MedicationDispense' },
            request: { method: 'PUT', url: `MedicationDispense/${(dispenseUpdate as any)?.id ?? dispenseId}` }
          });

          if (medicationUpdate) {
            entry.push({
              resource: { ...(medicationUpdate as any), resourceType: 'Medication' },
              request: { method: 'PUT', url: `Medication/${(medicationUpdate as any)?.id ?? medId}` }
            });
          }

          if (!chargeItem || !invoice) {
            // If we can't resolve billing artifacts, still cancel the dispense.
            return {
              resourceType: 'Bundle',
              type: 'transaction',
              entry
            } as any;
          }

          if (!isPaid) {
            // Unpaid: void billing artifacts (best-effort)
            const chargeItemUpdate: ChargeItem = { ...(chargeItem as any), status: 'entered-in-error' } as any;
            entry.push({
              resource: { ...(chargeItemUpdate as any), resourceType: 'ChargeItem' },
              request: { method: 'PUT', url: `ChargeItem/${(chargeItemUpdate as any).id}` }
            });

            const invoiceUpdate: Invoice = { ...(invoice as any), status: 'cancelled' } as any;
            entry.push({
              resource: { ...(invoiceUpdate as any), resourceType: 'Invoice' },
              request: { method: 'PUT', url: `Invoice/${(invoiceUpdate as any).id}` }
            });

            return {
              resourceType: 'Bundle',
              type: 'transaction',
              entry
            } as any;
          }

          // Paid: create reversal artifacts (credit) rather than rewriting the original invoice/charge.
          const amount = this.extractInvoiceGrossAmount(invoice) ?? this.extractChargeItemAmount(chargeItem);
          if (!amount) {
            // Still cancel dispense even if we can't compute refund amount
            return {
              resourceType: 'Bundle',
              type: 'transaction',
              entry
            } as any;
          }

          const currency = (amount.currency ?? this.extractInvoiceCurrency(invoice) ?? 'XOF').toString();
          const refundValue = -Math.abs(Number(amount.value ?? 0));

          const reversalChargeItemFullUrl = `urn:uuid:reversal-chargeitem-${dispenseId}-${Date.now()}`;
          const creditInvoiceFullUrl = `urn:uuid:credit-invoice-${dispenseId}-${Date.now()}`;

          const organizationId = this.authService.getOrganizationId();

          const codeableConcept =
            (chargeItem as any)?.code ??
            (dispense as any)?.medicationCodeableConcept ??
            ((dispense as any)?.medicationReference?.display
              ? { text: (dispense as any)?.medicationReference?.display }
              : { text: 'Medication dispense refund' });

          const reversalQuantity = this.cloneQuantity((chargeItem as any)?.quantity ?? (dispense as any)?.quantity);

          const reversalChargeItem: ChargeItem = {
            resourceType: 'ChargeItem',
            status: 'billed',
            code: codeableConcept,
            subject: (chargeItem as any)?.subject ?? (dispense as any)?.subject,
            ...((chargeItem as any)?.context ? { context: (chargeItem as any)?.context } : {}),
            // Link back to the original charge item
            partOf: [{ reference: `ChargeItem/${(chargeItem as any)?.id}` } as Reference],
            // Preserve the service linkage so it can still be found from the dispense
            service: (chargeItem as any)?.service ?? [{ reference: dispenseRef } as Reference],
            ...(reversalQuantity ? { quantity: reversalQuantity } : {}),
            priceOverride: { value: refundValue, currency } as Money,
            note: [{ text: 'Refund for cancelled dispense' } as any],
            ...(organizationId ? { performingOrganization: { reference: `Organization/${organizationId}` } } : {})
          } as any;

          entry.push({
            fullUrl: reversalChargeItemFullUrl,
            resource: reversalChargeItem,
            request: { method: 'POST', url: 'ChargeItem' }
          });

          // Phase 2: Reverse the informational Cost Price Snapshot (if it exists on original invoice)
          let costReversalComponent: any = null;
          if (invoice) {
            const originalLineItem = Array.isArray((invoice as any).lineItem) 
              ? (invoice as any).lineItem.find((l: any) => (l?.chargeItemReference?.reference ?? '') === `ChargeItem/${(chargeItem as any)?.id}`)
              : null;
              
            if (originalLineItem && Array.isArray(originalLineItem.priceComponent)) {
              const costSnapshot = originalLineItem.priceComponent.find((pc: any) => 
                pc.type === 'informational' && pc.code?.text === 'Cost Price Snapshot'
              );
              
              if (costSnapshot && costSnapshot.amount?.value) {
                costReversalComponent = {
                  type: 'informational',
                  code: { text: 'Cost Price Snapshot' },
                  amount: { 
                    value: -Math.abs(Number(costSnapshot.amount.value)), 
                    currency: costSnapshot.amount.currency || currency 
                  }
                };
              }
            }
          }

          const creditInvoice: Invoice = {
            resourceType: 'Invoice',
            status: 'issued',
            subject: (invoice as any)?.subject ?? (dispense as any)?.subject,
            date: new Date().toISOString(),
            lineItem: [
              {
                chargeItemReference: { reference: reversalChargeItemFullUrl } as any,
                ...(costReversalComponent ? { priceComponent: [costReversalComponent] } : {})
              } as any
            ],
            totalGross: { value: refundValue, currency } as Money,
            totalNet: { value: refundValue, currency } as Money,
            note: [{ text: `Refund for Invoice/${(invoice as any)?.id} (cancel dispense)` } as any]
          } as any;

          entry.push({
            fullUrl: creditInvoiceFullUrl,
            resource: creditInvoice,
            request: { method: 'POST', url: 'Invoice' }
          });

          // PaymentReconciliation to explicitly represent refund payment (best-effort).
          const pr: PaymentReconciliation = {
            resourceType: 'PaymentReconciliation',
            status: 'active',
            created: new Date().toISOString(),
            paymentDate: new Date().toISOString(),
            paymentAmount: { value: refundValue, currency } as Money,
            disposition: 'Refund payment for cancelled dispense',
            detail: [
              {
                type: { text: 'refund' } as any,
                amount: { value: refundValue, currency } as Money,
                request: { reference: `Invoice/${(invoice as any)?.id}` } as any
              } as any
            ]
          } as any;

          entry.push({
            resource: pr,
            request: { method: 'POST', url: 'PaymentReconciliation' }
          });

          return {
            resourceType: 'Bundle',
            type: 'transaction',
            entry
          } as any;
        })
      );
  }

  /**
    * Similar to prepareDispensePayloadEntry(), but it starts from a MedicationDispense id:
    * - Fetch MedicationDispense/{id}
    * - Fetch Medication/{id} (if medicationReference exists) and compute updated inventory
    * - Fetch MedicationRequest/{id} (if authorizingPrescription exists) and maybe mark completed
    * - Always update dispense.status => completed (PUT)
    */
  private prepareDispenseCompletionPayloadByDispenseId(dispenseId: string): Observable<{
    operationType: 'PUT';
    resourceType: 'MedicationDispense' | 'MedicationRequest' | 'Medication';
    objectToUse: MedicationDispense | MedicationRequest | Medication;
  }[]> {
    if (!dispenseId) {
      throw new Error('dispenseId is required.');
    }

    return this.http
      .get<MedicationDispense>(`${this.backendEndPoint}/MedicationDispense/${dispenseId}`, {
        context: new HttpContext().set(DisableCache, true)
      })
      .pipe(
        // 1) Normalize ids
        map((dispense) => {
          const medRefStr = this.coerceReference((dispense as any)?.medicationReference)?.reference || '';
          const medId = this.extractResourceId(medRefStr);

          const auth0 = Array.isArray((dispense as any)?.authorizingPrescription)
            ? (dispense as any).authorizingPrescription[0]
            : (dispense as any)?.authorizingPrescription;
          const mrRefStr = this.coerceReference(auth0)?.reference || '';
          const mrId = this.extractResourceId(mrRefStr);

          return { dispense, medId, mrId };
        }),

        // 2) Fetch related resources in parallel
        switchMap(({ dispense, medId, mrId }) =>
          forkJoin({
            medication: medId
              ? this.http.get<Medication>(`${this.backendEndPoint}/Medication/${medId}`, {
                context: new HttpContext().set(DisableCache, true)
              })
              : of(null),

            medRequest: mrId
              ? this.http.get<MedicationRequest>(`${this.backendEndPoint}/MedicationRequest/${mrId}`)
              : of(null)
          }).pipe(
            map(({ medication, medRequest }) => ({
              dispense,
              medId,
              mrId,
              medication,
              medRequest
            }))
          )
        ),

        // 3) Build updates
        map(({ dispense, medId, mrId, medication, medRequest }) => {
          const quantity = this.parseQuantity((dispense as any)?.quantity?.value ?? (dispense as any)?.quantity);
          console.log('[tracing med inventory deduction]', 'parsed quantity:', quantity);
          console.log('[tracing med inventory deduction]', 'this is the medication before deduction', medication);

          const medicationUpdate: Medication | undefined =
            medication && medId && quantity > 0
              ? (() => {
                    console.log('[tracing med inventory deduction]', 'this is the medication before deduction', medication);
                const updated = this.buildMedicationUpdatePayload(medication, quantity);
                if (!updated) return undefined;
                // Ensure id is present for PUT {resourceType}/{id}
                //before ded
            
                console.log('[tracing med inventory deduction]', 'this is the medication after deduction', updated);
                return { ...(updated as any), id: (updated as any)?.id ?? medId } as Medication;
              })()
              : undefined;

          const loggedInPractitionerId = this.authService.user.getValue()?.fhirResourceId;
          const currentPerformers = Array.isArray((dispense as any)?.performer) ? (dispense as any).performer : [];

          const dispenseUpdate: MedicationDispense = {
            ...(dispense as any),
            status: 'completed',
            whenHandedOver: new Date().toISOString(),
            performer: [
              ...currentPerformers,
              ...(loggedInPractitionerId ? [{
                function: {
                  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/medicationdispense-performer-function', code: 'finalchecker' }]
                },
                actor: { reference: `Practitioner/${loggedInPractitionerId}` }
              }] : [])
            ]
          };

          const shouldCompleteMr =
            !!mrId && this.shouldCompleteMedicationRequestBasedOnPerformerType(medRequest);

          const medicationRequestUpdate =
            shouldCompleteMr && medRequest
              ? ({
                ...(medRequest as any),
                status: 'completed'
              } as any)
              : undefined;

          return [
            {
              operationType: 'PUT' as const,
              resourceType: 'MedicationDispense' as const,
              objectToUse: dispenseUpdate
            },
            ...(medicationRequestUpdate
              ? [
                {
                  operationType: 'PUT' as const,
                  resourceType: 'MedicationRequest' as const,
                  objectToUse: medicationRequestUpdate!
                }
              ]
              : []),

            ...(medicationUpdate
              ? [
                {
                  operationType: 'PUT' as const,
                  resourceType: 'Medication' as const,
                  objectToUse: medicationUpdate
                }
              ]
              : [])

          ];
        })
      );

  }

  private shouldCompleteMedicationRequestBasedOnPerformerType(mr: MedicationRequest | null | undefined): boolean {
    if (!mr) return false;

    // If performerType is missing -> treat as patient (complete it)
    const performerType: any = (mr as any)?.performerType;
    if (!performerType) return true;

    // performerType can be CodeableConcept: check text or coding code/display
    const text = (performerType?.text ?? '').toString().trim().toLowerCase();
    const coding0 = performerType?.coding?.[0];
    const code = (coding0?.code ?? '').toString().trim().toLowerCase();
    const display = (coding0?.display ?? '').toString().trim().toLowerCase();

    // "patient" -> complete; anything else -> do nothing
    return text === 'patient' || code === 'patient' || display === 'patient';
  }

  private buildMedicationUpdatePayload(
    medication: Medication,
    quantity: number,
    opts?: { adjustment?: 'increment' | 'decrement' }
  ): Medication | undefined {
    console.log('[tracing med inventory deduction]', 'this is the medication before deduction', medication,);
    console.log('[tracing med inventory deduction]', 'this is the quantity to adjust by', quantity, 'with adjustment type', opts?.adjustment ?? 'decrement');
    const totalRemainingExt = this.findInventoryExtension(medication);

    if (!totalRemainingExt) return undefined;

    const cloned = JSON.parse(JSON.stringify(medication));
    const clonedField = this.findInventoryExtension(cloned);
    if (!clonedField) return undefined;

    const adjustment = opts?.adjustment ?? 'decrement';
    const current = Number(
      totalRemainingExt.valueQuantity?.value ??
      totalRemainingExt.valueInteger ??
      totalRemainingExt.valueDecimal ??
      totalRemainingExt.valueString ??
      0
    );
    console.log('[tracing med inventory deduction]', 'current inventory value:', current);
    const delta = adjustment === 'increment' ? Math.abs(quantity) : -Math.abs(quantity);
    const rawUpdatedValue = current + delta;
    console.log('[tracing med inventory deduction]', 'delta:', delta, 'rawUpdatedValue:', rawUpdatedValue);

    if (rawUpdatedValue < 0 && adjustment === 'decrement') {
      throw new Error(`Insufficient stock. Cannot deduct ${Math.abs(quantity)} because only ${current} remains.`);
    }

    const updatedValue = Math.max(0, rawUpdatedValue);

    if (clonedField.valueQuantity) {
      clonedField.valueQuantity.value = updatedValue;
    } else if (clonedField.valueInteger !== undefined) {
      clonedField.valueInteger = updatedValue;
    } else if (clonedField.valueDecimal !== undefined) {
      clonedField.valueDecimal = updatedValue;
    } else {
      clonedField.valueInteger = updatedValue;
    }

    if (current !== updatedValue) {
      if (!Array.isArray(cloned.extension)) cloned.extension = [];
      const unit = clonedField.valueQuantity?.unit || undefined;
      const url = `${this.terminologyBaseUrl.replace(/\/+$/, '')}/StructureDefinition/totalRemainingChanges`;

      cloned.extension.push({
        url,
        extension: [
          { url: 'dateChanged', valueDateTime: new Date().toISOString() },
          { url: 'practitionerWhoChanged', valueString: 'System (Medication Dispense)' },
          { url: 'totalRemainingBeforeChange', valueQuantity: { value: current, ...(unit ? { unit } : {}) } },
          { url: 'totalRemainingAfterChange', valueQuantity: { value: updatedValue, ...(unit ? { unit } : {}) } }
        ]
      });
    }

    console.log('[tracing med inventory deduction]', cloned);
    return cloned;
  }
  private findInventoryExtension(medication: Medication): any | undefined {
    return medication?.extension?.find((ext: any) =>
      typeof ext.url === 'string' && ext.url.endsWith('totalRemaining')
    );
  }

  private parseQuantity(value: any): number {
    if (value == null) return 0;
    const numeric = typeof value === 'object' && 'value' in value ? Number(value.value) : Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  private coerceReference(input: any): { reference: string; display?: string } | undefined {
    if (!input) return undefined;
    if (typeof input === 'string') {
      const parts = input.split('$#$').map(p => p.trim());
      return { reference: `${parts[0]}`.trim(), display: parts[1]?.trim() };
    }
    if (typeof input === 'object' && input.reference) {
      return { reference: input.reference, display: input.display };
    }
    return undefined;
  }

  private extractResourceId(reference?: string): string | undefined {
    if (!reference) return undefined;
    const parts = reference.split('/');
    return parts.length ? parts[parts.length - 1] : undefined;
  }

  private findChargeItemsForDispense(dispenseRef: string, subjectRef?: string): Observable<ChargeItem[]> {
    const tryServiceSearch$ = this.utilityService.fetchAll<ChargeItem>({
      resourceType: 'ChargeItem',
      queries: { service: dispenseRef },
      checkOrgWideState: false,
      updateOrgWideState: false
    }).pipe(
      catchError(() => of([] as ChargeItem[]))
    );

    return tryServiceSearch$.pipe(
      switchMap((found) => {
        if (Array.isArray(found) && found.length) return of(found);
        if (!subjectRef) return of([]);
        return this.utilityService.fetchAll<ChargeItem>({
          resourceType: 'ChargeItem',
          queries: { subject: subjectRef },
          checkOrgWideState: false,
          updateOrgWideState: false
        }).pipe(
          catchError(() => of([] as ChargeItem[]))
        );
      })
    );
  }

  private findInvoicesForSubject(subjectRef?: string): Observable<Invoice[]> {
    if (!subjectRef) return of([]);
    return this.utilityService.fetchAll<Invoice>({
      resourceType: 'Invoice',
      queries: { subject: subjectRef },
      checkOrgWideState: false,
      updateOrgWideState: false
    }).pipe(
      catchError(() => of([] as Invoice[]))
    );
  }

  private cloneQuantity(input: any): any | undefined {
    if (input == null) return undefined;

    if (typeof input === 'number') {
      const val = Number(input);
      return Number.isFinite(val) ? { value: val } : undefined;
    }

    if (typeof input === 'string') {
      const val = Number(input);
      return Number.isFinite(val) ? { value: val } : undefined;
    }

    if (typeof input === 'object') {
      try {
        return JSON.parse(JSON.stringify(input));
      } catch {
        const val = Number((input as any)?.value ?? NaN);
        return Number.isFinite(val) ? { value: val } : undefined;
      }
    }

    return undefined;
  }

  private extractInvoiceCurrency(invoice: Invoice | null): string | null {
    const gross = (invoice as any)?.totalGross;
    const net = (invoice as any)?.totalNet;
    return (gross?.currency ?? net?.currency ?? null) as any;
  }

  private extractInvoiceGrossAmount(invoice: Invoice | null): Money | null {
    if (!invoice) return null;
    const gross = (invoice as any)?.totalGross;
    if (gross && gross.value != null) return gross as Money;

    // Some servers only populate totalNet
    const net = (invoice as any)?.totalNet;
    if (net && net.value != null) return net as Money;

    return null;
  }

  private extractChargeItemAmount(ci: ChargeItem | null): Money | null {
    if (!ci) return null;
    const po = (ci as any)?.priceOverride;
    if (po && po.value != null) return po as Money;

    // In FHIR R4, the priced breakdown is typically on ChargeItemDefinition.propertyGroup[].priceComponent.
    // ChargeItem instances in this app generally rely on priceOverride; if it's missing, we can't safely infer price here.
    return null;
  }
}
